import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SeoMeta from '../../components/SeoMeta/SeoMeta';
import { isEduApexHost, isPathbuddyHost } from '../../config/eduHost';
import { STORAGE_KEYS } from '../../config/storageKeys';
import { clearAllPendingHandoffState } from './handoffContinuation';
import EduSiteHeader from './EduSiteHeader';
import EduSiteFooter from './EduSiteFooter';
import usePublicPage from '../auth/hooks/usePublicPage';
import EduLandingChatPrompt from './EduLandingChatPrompt';
import EduProductDeck from './EduProductDeck';
import EduLearningGraph from './EduLearningGraph';
import EduAppsGrid from './EduAppsGrid';
import useFadeInOnView from './useFadeInOnView';
import './EduLandingPage.css';

export default function EduLandingPage() {
  usePublicPage();
  const navigate = useNavigate();
  const location = useLocation();
  const hostname = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
  const onPathbuddy = isPathbuddyHost(hostname);
  const brandName = onPathbuddy ? 'Pathbuddy' : 'IVORI';
  const brandEduLabel = onPathbuddy ? 'Pathbuddy' : 'IVORI Edu';
  const canonicalOrigin =
    typeof window !== 'undefined' && isEduApexHost(hostname)
      ? window.location.origin
      : 'https://ivoriai.com/';

  const heroRef = useFadeInOnView();
  const chatSectionRef = useFadeInOnView();
  const deckHeadRef = useFadeInOnView();
  const deckTabsRef = useFadeInOnView();
  const deckViewportRef = useFadeInOnView();
  const graphHeadRef = useFadeInOnView();
  const graphCanvasRef = useFadeInOnView();
  const appsHeadRef = useFadeInOnView();
  const appsGridRef = useFadeInOnView();

  // Set by EduLandingChatPrompt via onHandoffPreservabilityChange, below —
  // a ref (not state) since it only needs to be read at click-time and
  // shouldn't trigger a re-render on every change. Deliberately not a
  // sessionStorage key: that would survive reloads, go stale, and recreate
  // the same class of bug this preservability check exists to fix.
  const hasPreservableHandoffRef = useRef(false);

  const handleStartFree = () => {
    if (hasPreservableHandoffRef.current) {
      // An anonymous visitor never has a legitimate retry token yet, even
      // when the handoff token/prompt id itself is being preserved.
      sessionStorage.removeItem(STORAGE_KEYS.PENDING_HANDOFF_RETRY_TOKEN);
    } else {
      clearAllPendingHandoffState();
    }
    navigate('/login?signup=true', { state: { returnTo: '/chat' } });
  };

  // Footer / deep links: /#edu-landing-deck|graph|apps → scroll after mount.
  useEffect(() => {
    const id = (location.hash || '').replace(/^#/, '');
    if (!id) return undefined;
    const t = window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }, 50);
    return () => window.clearTimeout(t);
  }, [location.hash, location.pathname]);

  return (
    <div className="edu-landing-root">
      <SeoMeta
        title={onPathbuddy ? 'Pathbuddy — Master any exam. Master any interview.' : 'edu.ivoriai.com — IVORI Edu'}
        description={`Master any exam. Master any interview. Tell ${brandName} what you're prepping for — it'll get you ready.`}
        keywords={onPathbuddy ? 'Pathbuddy, LNAT prep, TMUA prep, UCAT prep, TARA prep, ESAT prep, Oxbridge interview prep, exam prep AI' : 'IVORI Edu, LNAT prep, TMUA prep, UCAT prep, TARA prep, ESAT prep, Oxbridge interview prep, exam prep AI'}
        url={canonicalOrigin}
      />

      <EduSiteHeader onStartFree={handleStartFree} />

      <section className="edu-landing-hero" id="edu-landing-top">
        <div className="edu-landing-grain" />
        <div className="edu-landing-hero-inner edu-landing-fade-up" ref={heroRef}>
          <div className="edu-landing-eyebrow">{brandEduLabel}</div>
          <h1>
            <span className="edu-landing-hero-line">Master any exam.</span>
            <span className="edu-landing-hero-line">Master any interview.</span>
          </h1>
          <p className="edu-landing-hero-copy">
            Tell {brandName} what you're prepping for.
            <br />
            It'll get you ready.
          </p>
          <a
            className="edu-landing-hero-cta"
            href="#edu-landing-deck"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById('edu-landing-deck')?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            Explore
          </a>
        </div>
      </section>

      <EduLandingChatPrompt
        sectionRef={chatSectionRef}
        onHandoffPreservabilityChange={(isPreservable) => {
          hasPreservableHandoffRef.current = isPreservable;
        }}
      />

      <section className="edu-landing-section edu-landing-deck" id="edu-landing-deck">
        <div className="edu-landing-section-head edu-landing-fade-up" ref={deckHeadRef}>
          <div className="edu-landing-section-eyebrow">The Product Surfaces</div>
          <div className="edu-landing-section-title">Five ways in, one model underneath</div>
          <div className="edu-landing-section-sub">
            Every surface is built for exam and interview prep, depending on what you're getting ready
            for. For example: prep for LNAT, drill TMUA past papers, or mock a UBS interview.
          </div>
        </div>
        <EduProductDeck tabsRef={deckTabsRef} viewportRef={deckViewportRef} />
      </section>

      <section className="edu-landing-section" id="edu-landing-graph">
        <div className="edu-landing-section-head edu-landing-fade-up" ref={graphHeadRef}>
          <div className="edu-landing-section-eyebrow">Underneath It All</div>
          <div className="edu-landing-section-title">The learning graph</div>
          <div className="edu-landing-section-sub">
            Every question you drill, every mock interview you run, adds to a graph that tracks what
            you've practiced and what still needs work — so your prep gets sharper the more you use it.
          </div>
        </div>
        <div className="edu-landing-fade-up" ref={graphCanvasRef}>
          <EduLearningGraph />
        </div>
      </section>

      <section className="edu-landing-section" id="edu-landing-apps">
        <div className="edu-landing-section-head edu-landing-fade-up" ref={appsHeadRef}>
          <div className="edu-landing-section-eyebrow">Apps</div>
          <div className="edu-landing-section-title">Exam and admissions prep, decision by decision</div>
          <div className="edu-landing-section-sub">
            Dedicated apps for the tests and applications that gate your next step — each one built on
            the same underlying model.
          </div>
        </div>
        <div className="edu-landing-fade-up" ref={appsGridRef}>
          <EduAppsGrid />
        </div>
      </section>

      <EduSiteFooter />
    </div>
  );
}
